import React, { useState, useEffect, useRef, useCallback, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { isCapacitorNative } from '@/lib/capacitor';
import { clearSensitiveStorage } from '@/lib/signOutStorage';
import { ConnectionErrorScreen } from '@/components/ConnectionErrorScreen';

const INITIAL_SESSION_TIMEOUT_MS = 10_000;

/** Returns the primary login provider: 'google' | 'apple' | 'email' | null */
export function getLoginProvider(user: User | null): 'google' | 'apple' | 'email' | null {
  if (!user?.identities?.length) return null;
  const provider = user.identities[0]?.provider;
  if (provider === 'google' || provider === 'apple') return provider;
  return 'email';
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  /** True when initial session check failed (timeout or network). Show error UI and Retry. */
  connectionError: boolean;
  /** Retry initial session check after connection error. */
  retryInitialSession: () => void;
  /** Primary login method: 'google' | 'apple' | 'email'. OAuth users sign in with that provider only. */
  loginProvider: 'google' | 'apple' | 'email' | null;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signInWithApple: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const isSigningOutRef = useRef(false);

  const runInitialSessionCheck = useCallback(() => {
    setLoading(true);

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Session check timeout')), INITIAL_SESSION_TIMEOUT_MS);
    });

    Promise.race([
      supabase.auth.getSession().then(({ data: { session: s } }) => s),
      timeoutPromise,
    ])
      .then((s) => {
        setSession(s ?? null);
        setUser(s?.user ?? null);
        setConnectionError(false);
      })
      .catch(() => {
        setConnectionError(true);
        setSession(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        setConnectionError(false);

        // Session expired or token refresh failed: redirect to auth with param so Auth page can show toast
        if (event === 'SIGNED_OUT' && !session && typeof window !== 'undefined') {
          const pathname = window.location.pathname || '';
          const isAuthPage = pathname === '/auth' || pathname.endsWith('/auth');
          if (!isAuthPage && !isSigningOutRef.current) {
            const origin = window.location.origin;
            const authPath = isCapacitorNative() ? 'auth' : '/auth';
            const url = isCapacitorNative()
              ? `luma://auth?expired=1`
              : `${origin}/auth?expired=1`;
            window.location.href = url;
          }
        }
      }
    );

    runInitialSessionCheck();

    return () => subscription.unsubscribe();
  }, [runInitialSessionCheck]);

  const getRedirectTo = (path: string) => {
    if (isCapacitorNative() && typeof window !== 'undefined') {
      return `luma://${path.replace(/^\//, '')}`;
    }
    if (typeof window === 'undefined') {
      return path.startsWith('/') ? path : `/${path}`;
    }
    return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = getRedirectTo('/');
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return { error: error as Error | null };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: getRedirectTo('/app'),
      }
    });
    
    return { error: error as Error | null };
  };

  const signInWithApple = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: getRedirectTo('/app'),
      }
    });
    
    return { error: error as Error | null };
  };

  const signOut = async () => {
    isSigningOutRef.current = true;
    clearSensitiveStorage();
    await supabase.auth.signOut();
    if (typeof window !== 'undefined') {
      const path = isCapacitorNative() ? 'auth' : '/auth';
      window.location.href = isCapacitorNative() ? `luma://${path}` : `${window.location.origin}/auth`;
    }
  };

  const loginProvider = getLoginProvider(user);

  return (
    <AuthContext.Provider value={{ user, session, loading, connectionError, retryInitialSession: runInitialSessionCheck, loginProvider, signUp, signIn, signInWithGoogle, signInWithApple, signOut }}>
      {connectionError && !user ? (
        <ConnectionErrorScreen onRetry={runInitialSessionCheck} isRetrying={loading} />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
